import { apiPaths } from 'api/kinerja/kepala-sekolah/config'
import { useKinerjaApi, useKinerjaMutationApi } from 'api/kinerja/utils/api'

import type { CommonResponse } from 'types/api'
import type { PelaksanaanError } from 'types/kinerja-ks-pelaksanaan'
import type {
  TPayloadPenilaianForDelivery,
  TPenilaianFilterStatus,
  TPenilaianTujuanState,
  TResponsePenilaian,
  TResponsePenilaianForDelivery,
  TResponsePenilaianSummary,
  TResponsePenilaianCount,
  TPayloadPenilaian,
} from 'types/kinerja-ps'
import type { UseMutationOptions, UseQueryOptions } from 'react-query'

export const API_GET_KS_PENILAIAN_BY_PS_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_PS_QUERY_KEY'

export const API_GET_KS_PENILAIAN_COUNT_BY_PS_QUERY_KEY =
  'API_GET_KS_PENILAIAN_COUNT_BY_PS_QUERY_KEY'

export const API_GET_KS_PENILAIAN_BY_PS_SUMMARY_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_PS_SUMMARY_QUERY_KEY'

export const API_GET_KS_PENILAIAN_BY_PS_SUMMARY_FOR_GURU_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_PS_SUMMARY_FOR_GURU_QUERY_KEY'

type TPenilaianSummaryPsProp = {
  state?: TPenilaianTujuanState
}

type TPenilaianPsProp = TPenilaianSummaryPsProp & {
  name?: string
  status?: TPenilaianFilterStatus[]
  confirmedOnly?: boolean
}

export function useApiGetPenilaianPs(
  params: TPenilaianPsProp,
  opts?: UseQueryOptions<TResponsePenilaian>
) {
  const nameParam = params?.name || undefined

  const status: TPenilaianFilterStatus[] = params?.status || ['ALL']
  const statusParam = status ? status?.join(',') : undefined

  const stateParam = params?.state || undefined

  // Intentionally exclude `false` from the query string so the API can handle it according to its default behavior
  const confirmedOnlyParam = params?.confirmedOnly === true || undefined

  return useKinerjaApi<TResponsePenilaian>(apiPaths.penilaianPengawasSekolah, {
    axiosConfig: {
      params: {
        name: nameParam,
        statusPengiriman: statusParam,
        state: stateParam,
        confirmedOnly: confirmedOnlyParam,
      },
    },
    queryConfig: {
      useErrorBoundary: false,
      ...opts,
    },
    queryKey: [
      API_GET_KS_PENILAIAN_BY_PS_QUERY_KEY,
      nameParam,
      statusParam,
      stateParam,
      confirmedOnlyParam,
    ],
  })
}

export function useApiGetPenilaianCountPs(
  opts?: UseQueryOptions<TResponsePenilaianCount>
) {
  return useKinerjaApi<TResponsePenilaianCount>(
    `${apiPaths.penilaianPengawasSekolah}/count`,
    {
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_COUNT_BY_PS_QUERY_KEY],
    }
  )
}

export function useApiGetPenilaianSummaryPs(
  params: TPenilaianSummaryPsProp,
  opts?: UseQueryOptions<TResponsePenilaianSummary>
) {
  const stateParam = params?.state || undefined

  return useKinerjaApi<TResponsePenilaianSummary>(
    `${apiPaths.penilaianPengawasSekolah}/summary`,
    {
      axiosConfig: {
        params: {
          state: stateParam,
        },
      },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_BY_PS_SUMMARY_QUERY_KEY, stateParam],
    }
  )
}
export function useApiPostPenilaianForDelivery(
  opts?: UseMutationOptions<
    CommonResponse<TResponsePenilaianForDelivery>,
    PelaksanaanError,
    TPayloadPenilaianForDelivery
  >
) {
  return useKinerjaMutationApi(apiPaths.penilaianPengawasSekolah, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}

export function useApiPatchPenilaianPs(
  opts?: UseMutationOptions<
    CommonResponse<any>,
    PelaksanaanError,
    TPayloadPenilaian
  >
) {
  return useKinerjaMutationApi(
    `${apiPaths.penilaianPengawasSekolah}/override`,
    {
      axiosConfig: {
        method: 'PATCH',
      },
      mutationConfig: {
        useErrorBoundary: false,
        ...opts,
      },
    }
  )
}
